'use client';

import React, {useCallback, useEffect, useRef} from 'react';

type MouseEventHandler =
  | 'onClick'
  | 'onMouseDown'
  | 'onMouseUp'
  | 'onPointerDown'
  | 'onPointerUp';

type TouchEventHandler = 'onTouchStart' | 'onTouchEnd';

export interface ClickAwayListenerProps {
  children: React.ReactElement;
  isDisabledInsideTree?: boolean;
  mouseEvent?: MouseEventHandler | false;
  onClickAway: (event: MouseEvent | TouchEvent) => void;
  touchEvent?: TouchEventHandler | false;
}

const isScrollbarClick = (event: MouseEvent, doc: Document) => {
  const {clientHeight, clientWidth} = doc.documentElement;

  return clientWidth < event.clientX || clientHeight < event.clientY;
};

const ClickAwayListener = ({
  children,
  isDisabledInsideTree = false,
  mouseEvent = 'onClick',
  touchEvent = 'onTouchEnd',
  onClickAway,
}: ClickAwayListenerProps) => {
  const elementRef = useRef<Element | null>(null);
  const isMovedRef = useRef(false);
  const isSyntheticRef = useRef(false);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    setTimeout(() => {
      isInitializedRef.current = true;
    }, 0);

    return () => {
      isInitializedRef.current = false;
    };
  }, []);

  const handleRef = useCallback((node: Element | null) => {
    elementRef.current = node;
  }, []);

  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (
        !isInitializedRef.current ||
        !elementRef.current ||
        ('clientX' in event && isScrollbarClick(event, document))
      ) {
        return;
      }

      if (isSyntheticRef.current) isSyntheticRef.current = false;

      if (
        elementRef.current.contains(event.target as Node) ||
        isMovedRef.current
      ) {
        isMovedRef.current = false;
        return;
      }

      const isInsideDOM = event.composedPath
        ? event.composedPath().includes(elementRef.current)
        : document.documentElement.contains(event.target as Node) &&
          elementRef.current.contains(event.target as Node);

      if (!isInsideDOM && (isDisabledInsideTree || !isSyntheticRef.current)) {
        onClickAway(event);
      }
    },
    [onClickAway, isDisabledInsideTree],
  );

  const createSyntheticHandler = useCallback(
    (handlerName: string) => (event: React.SyntheticEvent) => {
      isSyntheticRef.current = true;

      const childrenPropsHandler = (
        children.props as Record<string, (e: React.SyntheticEvent) => void>
      )[handlerName];
      if (childrenPropsHandler) {
        childrenPropsHandler(event);
      }
    },
    [children],
  );

  const childrenProps: {ref: (node: Element | null) => void} & Partial<
    Record<
      MouseEventHandler | TouchEventHandler,
      (event: React.SyntheticEvent) => void
    >
  > = {ref: handleRef};

  if (touchEvent !== false) {
    childrenProps[touchEvent] = createSyntheticHandler(touchEvent);
  }

  if (mouseEvent !== false) {
    childrenProps[mouseEvent] = createSyntheticHandler(mouseEvent);
  }

  useEffect(() => {
    const handleMove = () => {
      isMovedRef.current = true;
    };

    const eventMouse = mouseEvent ? mouseEvent.substring(2).toLowerCase() : '';
    const eventTouch = touchEvent ? touchEvent.substring(2).toLowerCase() : '';

    if (mouseEvent !== false) {
      document.addEventListener(eventMouse, handleClickAway as EventListener);
    }

    if (touchEvent !== false) {
      document.addEventListener(eventTouch, handleClickAway as EventListener);
      document.addEventListener('touchmove', handleMove as EventListener);
    }

    return () => {
      if (mouseEvent !== false) {
        document.removeEventListener(
          eventMouse,
          handleClickAway as EventListener,
        );
      }

      if (touchEvent !== false) {
        document.removeEventListener(
          eventTouch,
          handleClickAway as EventListener,
        );
        document.removeEventListener('touchmove', handleMove as EventListener);
      }
    };
  }, [mouseEvent, touchEvent, handleClickAway]);

  return <>{React.cloneElement(children, childrenProps)}</>;
};
export {ClickAwayListener};
